import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { AppIcon, AppIconName } from '../icons/icon.type';
import { TouchHoverDirective } from 'core/directives/touch-hover.directive';
import { TOUCH_INTERACTION_DELAY_MS } from 'core/constants/touch-interaction';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[appButton]',
  template: `
    @if (appButton) {
      <app-icon class="icon {{ iconClasses }}" [icon]="appButton"></app-icon>
    }
    @if (isoCode) {
      <app-flag [code]="isoCode"></app-flag>
    }
    <span class="content" [ngClass]="appButton || isoCode ? 'with-icon' : ''">
      <ng-content></ng-content>
    </span>
    <app-icon class="icon ri {{ iconClasses }}" *ngIf="secondIcon" [icon]="secondIcon"></app-icon>
  `,
  styleUrls: ['./styles/button.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [TouchHoverDirective],
})
export class ButtonComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input()
  appButton?: AppIconName | AppIcon = '';

  @Input()
  secondIcon?: AppIconName | AppIcon = '';

  @Input()
  isoCode?: string;

  @Input()
  label?: string;

  @Input()
  iconClasses?: string = '';

  @Input()
  action!: () => Subscription | void;

  @Output()
  touchClick = new EventEmitter<MouseEvent>();

  private _form: HTMLFormElement | null = null;
  private _disabled = false;
  private lastTouchTime = 0;
  private touchClickTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public elementRef: ElementRef<HTMLButtonElement>,
    renderer: Renderer2,
  ) {
    renderer.addClass(elementRef.nativeElement, 'app-control');
  }

  ngOnInit() {
    const btnEl = this.elementRef.nativeElement;
    // If has no explicit type, default to "button"
    if (btnEl.tagName === 'BUTTON' && !btnEl.hasAttribute('type')) {
      btnEl.setAttribute('type', 'button');
    }
  }

  ngAfterViewInit() {
    if (this.elementRef.nativeElement.type === 'submit') {
      this._form = this.elementRef.nativeElement.closest('form');
      if (this._form) {
        this._form.addEventListener('submit', this.onFormSubmit, { passive: false });
      }
    }
  }

  ngOnDestroy() {
    if (this._form) {
      this._form.removeEventListener('submit', this.onFormSubmit);
    }
    if (this.touchClickTimeoutId) {
      clearTimeout(this.touchClickTimeoutId);
    }
  }

  @HostListener('touchstart')
  onTouchStart() {
    this.lastTouchTime = Date.now();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.touchClick.observers.length) {
      if (this.shouldDelayTouchClick()) {
        this.scheduleTouchClick(event);
        return;
      }
      this.touchClick.emit(event);
      return;
    }
    if (this.elementRef.nativeElement.type !== 'submit') {
      this.runAction(event);
    }
  }

  private onFormSubmit(event: SubmitEvent) {
    // Only proceed if this button was the submitter (supports Enter key use)
    const isThisButton = event.submitter === this.elementRef?.nativeElement;
    if (!isThisButton) return;
    this.runAction(event);
  }

  private runAction(event: Event) {
    if (this._disabled || !this.action) return;

    const result = this.action();
    if (!result) {
      this.setDisabled(false);
      event.preventDefault();
      return;
    }
    this.setDisabled(true);
    if ('add' in result && typeof result.add === 'function') {
      result.add(() => this.setDisabled(false));
      event.preventDefault();
      return;
    }
    this.setDisabled(false);
    event.preventDefault();
  }

  private shouldDelayTouchClick(): boolean {
    return Date.now() - this.lastTouchTime < TOUCH_INTERACTION_DELAY_MS;
  }

  private scheduleTouchClick(event: MouseEvent): void {
    if (this.touchClickTimeoutId) {
      clearTimeout(this.touchClickTimeoutId);
    }
    this.touchClickTimeoutId = setTimeout(() => {
      this.touchClick.emit(event);
      this.touchClickTimeoutId = null;
    }, TOUCH_INTERACTION_DELAY_MS);
  }

  private setDisabled(state: boolean) {
    this._disabled = state;
    if (state) {
      this.elementRef.nativeElement.setAttribute('disabled', '');
    } else {
      this.elementRef.nativeElement.removeAttribute('disabled');
    }
  }
}
